/**
 * フォーム回答「統計」タブ。セクションを追加してチャート表示。
 * v3.1.4
 */
import type {
  FieldAggregation,
  FormAnalyticsVisualStyle,
  FormSubmission,
} from '@goenhub/shared';
import { aggregateField, extractFieldElements } from '@goenhub/shared';

import type { FormRecord } from '@/hooks/useForms';
import { BarChart3, Copy, Edit, ExternalLink, Plus, Share2, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { EmptyState } from '@/components/common/EmptyState';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useDeleteAnalyticsSection,
  useFormAnalyticsMeta,
  useFormAnalyticsSections,
  useUpsertAnalyticsMeta,
  type FormAnalyticsSectionRecord,
} from '@/hooks/useFormAnalytics';
import { paths } from '@/routes/paths';

import { AddSectionDialog } from './AddSectionDialog';
import { CHART_TYPE_LABEL, ChartRenderer } from './ChartRenderer';
import { VISUAL_STYLES, resolveStyle } from './visualStyles';

interface Props {
  form: FormRecord;
  submissions: Array<Pick<FormSubmission, 'values'>>;
}

function genToken(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function SubmissionsAnalyticsTab({ form, submissions }: Props) {
  const { user } = useAuth();
  const sections = useFormAnalyticsSections(form.id);
  const meta = useFormAnalyticsMeta(form.id);
  const deleteSection = useDeleteAnalyticsSection(form.id);
  const upsertMeta = useUpsertAnalyticsMeta(form.id);

  const [dialog, setDialog] = useState<{
    open: boolean;
    existing: FormAnalyticsSectionRecord | null;
  }>({ open: false, existing: null });

  const visualStyle: FormAnalyticsVisualStyle = meta.data?.visualStyle ?? 'minimal';
  const style = resolveStyle(visualStyle);

  // 各 fieldId の集計を毎回計算 (件数が小さいので OK)
  const fieldElements = useMemo(
    () => extractFieldElements(form.elements),
    [form.elements],
  );
  const fieldMap = useMemo(() => {
    const m = new Map(fieldElements.map((f) => [f.id, f]));
    return m;
  }, [fieldElements]);

  function getAggsForSection(fieldIds: string[]): FieldAggregation[] {
    // 統計タブは admin (rules 内) なので includeText:true で OK。
    // 公開ビュー (Cloud Function 側) では includeText:false で送る。
    return fieldIds
      .map((id) => fieldMap.get(id))
      .filter((f): f is NonNullable<typeof f> => !!f)
      .map((f) => aggregateField(f, submissions, { includeText: true }));
  }

  const handleStyleChange = async (next: FormAnalyticsVisualStyle) => {
    if (!user?.uid) return;
    try {
      await upsertMeta.mutateAsync({ visualStyle: next, uid: user.uid });
    } catch (err) {
      toast.error(`保存失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleEnableShare = async () => {
    if (!user?.uid) return;
    try {
      const token = meta.data?.publicToken ?? genToken();
      await upsertMeta.mutateAsync({ publicToken: token, uid: user.uid });
      const url = `${window.location.origin}${paths.publicFormAnalytics.replace(':token', token)}`;
      await navigator.clipboard.writeText(url);
      toast.success('公開URLをコピーしました');
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleDisableShare = async () => {
    if (!user?.uid) return;
    if (!window.confirm('公開URLを無効化します。よろしいですか？')) return;
    try {
      await upsertMeta.mutateAsync({ publicToken: null, uid: user.uid });
      toast.success('公開URLを無効化しました');
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleDelete = async (s: FormAnalyticsSectionRecord) => {
    if (!window.confirm(`セクション「${s.title || CHART_TYPE_LABEL[s.chartType]}」を削除します。よろしいですか？`)) return;
    try {
      await deleteSection.mutateAsync(s.id);
      toast.success('削除しました');
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const publicUrl = meta.data?.publicToken
    ? `${window.location.origin}${paths.publicFormAnalytics.replace(':token', meta.data.publicToken)}`
    : null;

  if (sections.isLoading || meta.isLoading) return <Spinner />;

  const sectionList = sections.data ?? [];
  const nextOrder = sectionList.length > 0
    ? Math.max(...sectionList.map((s) => s.displayOrder)) + 10
    : 10;

  return (
    <div className="space-y-4">
      {/* ツールバー */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-[var(--text-muted)]">スタイル:</span>
          <select
            value={visualStyle}
            onChange={(e) =>
              handleStyleChange(e.target.value as FormAnalyticsVisualStyle)
            }
            className="rounded border border-[var(--border)] bg-[var(--bg-surface)] px-2 py-1 text-xs"
          >
            {Object.entries(VISUAL_STYLES).map(([key, preset]) => (
              <option key={key} value={key}>
                {preset.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex-1" />

        {publicUrl ? (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(publicUrl);
                toast.success('公開URLをコピーしました');
              }}
              title="公開URLをコピー"
            >
              <Copy />
              URLをコピー
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => window.open(publicUrl, '_blank')}
              title="公開ページを開く"
            >
              <ExternalLink />
              プレビュー
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleDisableShare}
            >
              共有を停止
            </Button>
          </>
        ) : (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleEnableShare}
          >
            <Share2 />
            共有URLを発行
          </Button>
        )}

        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => setDialog({ open: true, existing: null })}
        >
          <Plus />
          セクション追加
        </Button>
      </div>

      {sectionList.length === 0 ? (
        <EmptyState
          icon={BarChart3}
          title="統計セクションがありません"
          description="「セクション追加」から、回答を可視化するチャートを作成できます。"
        />
      ) : (
        <div
          className="rounded-md p-4 space-y-4"
          style={{
            backgroundColor: style.cardBackground,
            border: `1px solid ${style.cardBorder}`,
            fontFamily: style.fontFamily,
          }}
        >
          {sectionList.map((s) => {
            const aggs = getAggsForSection(s.fieldIds);
            const sectionTitle =
              s.title || CHART_TYPE_LABEL[s.chartType];
            return (
              <div
                key={s.id}
                className="rounded-md p-4"
                style={{
                  backgroundColor: style.cardBackground,
                  border: `1px solid ${style.cardBorder}`,
                }}
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <h3
                      className="text-base font-semibold"
                      style={{ color: style.titleColor }}
                    >
                      {sectionTitle}
                    </h3>
                    <p
                      className="text-xs mt-0.5"
                      style={{ color: style.textColor }}
                    >
                      {aggs.map((a) => a.label).join(' / ')}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 no-print">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setDialog({ open: true, existing: s })}
                      title="編集"
                    >
                      <Edit />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(s)}
                      title="削除"
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </div>
                <ChartRenderer
                  chartType={s.chartType}
                  aggregations={aggs}
                  style={style}
                />
              </div>
            );
          })}
        </div>
      )}

      <AddSectionDialog
        open={dialog.open}
        formId={form.id}
        form={form}
        existing={dialog.existing}
        defaultOrder={nextOrder}
        onOpenChange={(o) => setDialog((s) => ({ ...s, open: o }))}
      />
    </div>
  );
}
